import { Controller } from "@hotwired/stimulus"

const SORT_STATE = {
  ASCENDING: "ascending",
  DESCENDING: "descending",
  NONE: "none"
}

const NEXT_SORT_STATE = {
  [SORT_STATE.ASCENDING]: SORT_STATE.DESCENDING,
  [SORT_STATE.DESCENDING]: SORT_STATE.NONE,
  [SORT_STATE.NONE]: SORT_STATE.ASCENDING
}

export default class extends Controller {
  static targets = ["modal", "title", "caption", "tableBody", "headerCell", "sortButton", "tableRegion"]

  open(event) {
    event.preventDefault()

    const params = event.params
    this.courses = this.coursesFromHost(event) ?? this.parseArrayParam(params.courses)
    this.titleTarget.textContent = params.label || "Courses Using this Case"
    this.sortColumnKey = null
    this.sortDirection = SORT_STATE.NONE
    this.resetHeaderSortState()
    this.renderTableBody()
    this.syncDialogDescription()
    this.modalController.show(event)
  }

  get modalController() {
    return this.application.getControllerForElementAndIdentifier(this.modalTarget, "railsui-modal")
  }

  sortColumn(event) {
    if (!this.shouldActivateSortColumn(event)) return

    if (event.type === "keydown") {
      event.preventDefault()
      event.stopPropagation()
      this.sortKeyboardActivation = true
      queueMicrotask(() => {
        this.sortKeyboardActivation = false
      })
    } else if (event.type === "click") {
      if (event.detail === 0 && this.sortKeyboardActivation) return

      event.stopPropagation()
    }

    const columnKey = event.params.column
    if (!columnKey) return

    if (this.sortColumnKey === columnKey) {
      this.sortDirection = NEXT_SORT_STATE[this.sortDirection]
    } else {
      this.sortColumnKey = columnKey
      this.sortDirection = SORT_STATE.ASCENDING
    }

    if (this.sortDirection === SORT_STATE.NONE) {
      this.sortColumnKey = null
    }

    this.applyHeaderSortState()
    this.renderTableBody()
    this.announceSort(event.params.columnLabel || columnKey)
  }

  shouldActivateSortColumn(event) {
    if (event.type === "keydown") {
      return event.key === "Enter" || event.key === " " || event.key === "Spacebar"
    }

    return event.type === "click"
  }

  parseArrayParam(value) {
    if (Array.isArray(value)) return value
    if (value == null || value === "") return []

    try {
      const parsed = JSON.parse(value)
      return Array.isArray(parsed) ? parsed : []
    } catch (_error) {
      return []
    }
  }

  coursesFromHost(event) {
    const host = event.currentTarget.closest("[data-case-card-courses]")
    if (!host?.dataset.caseCardCourses) return null

    try {
      const parsed = JSON.parse(host.dataset.caseCardCourses)
      return Array.isArray(parsed) ? parsed : []
    } catch (_error) {
      return []
    }
  }

  renderTableBody() {
    const rows = this.sortedCourses()
    this.captionTarget.textContent = `${rows.length} courses`
    this.tableBodyTarget.innerHTML = rows.map((course) => this.renderRow(course)).join("")
  }

  sortedCourses() {
    if (!this.sortColumnKey || this.sortDirection === SORT_STATE.NONE) {
      return [...this.courses]
    }

    const direction = this.sortDirection === SORT_STATE.ASCENDING ? 1 : -1

    return [...this.courses].sort((left, right) => {
      const comparison = this.compareSortableValues(
        this.sortableValue(left, this.sortColumnKey),
        this.sortableValue(right, this.sortColumnKey)
      )

      return comparison * direction
    })
  }

  sortableValue(course, columnKey) {
    switch (columnKey) {
      case "course_name":
        return course.course_name || ""
      case "responsible_person":
        return [course.responsible_person, course.email].filter(Boolean).join(" ")
      case "start_date":
        return course.start_date || ""
      case "end_date":
        return course.end_date || ""
      default:
        return ""
    }
  }

  compareSortableValues(left, right) {
    const leftDate = Date.parse(left)
    const rightDate = Date.parse(right)
    if (!Number.isNaN(leftDate) && !Number.isNaN(rightDate)) {
      return leftDate - rightDate
    }

    return String(left).localeCompare(String(right), undefined, {
      numeric: true,
      sensitivity: "base"
    })
  }

  renderRow(course) {
    const responsiblePersonCell = this.renderResponsiblePersonCell(course)

    return `
      <tr>
        ${this.renderDataCell(this.renderCourseNameCell(course), 0)}
        ${this.renderDataCell(responsiblePersonCell, 1, { stacked: true })}
        ${this.renderDataCell(this.escapeHtml(course.start_date || ""), 2)}
        ${this.renderDataCell(this.escapeHtml(course.end_date || ""), 3)}
      </tr>
    `
  }

  renderDataCell(content, _index, { stacked = false } = {}) {
    const innerClass = stacked
      ? "aqui-base-table__cell-inner aqui-base-table__cell-inner--stack"
      : "aqui-base-table__cell-inner"

    return `
      <td class="aqui-base-table__cell">
        <div class="${innerClass}">
          ${content}
        </div>
      </td>
    `
  }

  renderCourseNameCell(course) {
    const label = this.escapeHtml(course.course_name || "")
    const href = this.escapeHtml(this.safeHref(course.course_href))

    return `<a href="${href}" class="aqui-base-table__cell-link">${label}</a>`
  }

  renderResponsiblePersonCell(course) {
    const parts = []

    if (course.responsible_person) {
      parts.push(`<span>${this.escapeHtml(course.responsible_person)}</span>`)
    }

    if (course.email) {
      const href = this.escapeHtml(this.safeHref(course.email_href || `mailto:${course.email}`, "#"))
      parts.push(
        `<a href="${href}" class="aqui-base-table__cell-link">${this.escapeHtml(course.email)}</a>`
      )
    }

    return parts.join("") || '<span class="text-[var(--ds-text-primary)]">&nbsp;</span>'
  }

  safeHref(href, fallback = "#") {
    const value = String(href || "").trim()
    if (!value) return fallback
    if (value.startsWith("/") || value.startsWith("#") || value.startsWith("?")) return value

    try {
      const url = new URL(value, window.location.origin)
      if (["http:", "https:", "mailto:", "tel:"].includes(url.protocol)) return value
    } catch (_error) {
      return fallback
    }

    return fallback
  }

  resetHeaderSortState() {
    this.headerCellTargets.forEach((headerCell) => {
      headerCell.setAttribute("aria-sort", SORT_STATE.NONE)
    })

    this.sortButtonTargets.forEach((button) => {
      const label = button.dataset.columnLabel || "column"
      button.setAttribute("aria-label", `Sort by ${label}`)
    })
  }

  applyHeaderSortState() {
    this.headerCellTargets.forEach((headerCell) => {
      const columnKey = headerCell.dataset.columnKey
      const sortState = columnKey === this.sortColumnKey ? this.sortDirection : SORT_STATE.NONE
      headerCell.setAttribute("aria-sort", sortState)

      const button = headerCell.querySelector("[data-case-card-courses-using-case-modal-target='sortButton']")
      if (!button) return

      const label = button.dataset.columnLabel || columnKey
      button.setAttribute("aria-label", `Sort by ${label}`)
    })
  }

  announceSort(columnLabel) {
    if (!this.hasTableRegionTarget) return

    if (!this.sortColumnKey || this.sortDirection === SORT_STATE.NONE) {
      this.tableRegionTarget.setAttribute("aria-label", `${columnLabel}, sort cleared`)
      return
    }

    this.tableRegionTarget.setAttribute(
      "aria-label",
      `${columnLabel}, sorted ${this.sortDirection}`
    )
  }

  syncDialogDescription() {
    const container = this.modalTarget.querySelector("[data-railsui-modal-target='container']")
    if (!container) return

    const describedby = [this.captionTarget.id].filter(Boolean).join(" ")
    if (describedby) {
      container.setAttribute("aria-describedby", describedby)
    }
  }

  escapeHtml(value) {
    return String(value)
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#39;")
  }
}
